import LinkOutlined from '@ant-design/icons/LinkOutlined'
import { Link, LinkProps } from '@tanstack/react-router'
import { Tooltip } from 'antd'
import { createFieldSetter } from 'features/libra/stepComponents/niiReport/createFieldSetter'
import {
  EditableDate,
  EditableString,
} from 'features/libra/stepComponents/niiReport/NiiFieldComponents'
import { useNiiReportData } from 'features/libra/stepComponents/niiReport/NiiReportDataContext'
import {
  DescriptionItem,
  OptionalDescriptions,
} from 'features/libra/stepComponents/niiReport/OptionalDescriptions'
import { useReferenceValues } from 'features/libra/stepComponents/niiReport/referenceFields'
import React, { FC, useMemo } from 'react'
import styled from 'styled-components'

import { StyledCard } from './cardsStyles'

const LinkRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`

const LinkTag: FC<{
  to: LinkProps['to']
  params?: LinkProps['params']
  ariaLabel: string
  tooltipTitle: string
}> = ({ to, params, ariaLabel, tooltipTitle }) => {
  return (
    <Tooltip title={tooltipTitle}>
      <Link
        to={to}
        params={params}
        target='_blank'
        rel='noreferrer'
        aria-label={ariaLabel}>
        <LinkOutlined />
      </Link>
    </Tooltip>
  )
}

export const RawNiiBasicInformationCard: FC = () => {
  const { assetModelData, niiData, setNiiData, isLoading } = useNiiReportData()

  const setAnalysisDate = useMemo(
    () => createFieldSetter('analysisDate', setNiiData),
    [setNiiData]
  )

  const unitId = assetModelData.unit?.id
  const siteId = assetModelData.unit?.tags.site

  const { values: analysisDateReferenceValues } =
    useReferenceValues('analysisDate')

  const items = useMemo<ReadonlyArray<DescriptionItem>>(
    () => [
      [
        'Site',
        <LinkRow key='site'>
          <EditableString value={assetModelData.siteName} />
          {siteId && (
            <LinkTag
              to='/assets/{-$siteID}'
              params={{ siteID: siteId }}
              ariaLabel='Open site in asset database'
              tooltipTitle='Open Site'
            />
          )}
        </LinkRow>,
      ] as const,
      [
        'Asset',
        <LinkRow key='asset'>
          <EditableString value={assetModelData.unit?.data.name} />
          {siteId && unitId && (
            <LinkTag
              to='/assets/$siteID/$unitID'
              params={{ siteID: siteId, unitID: unitId }}
              ariaLabel='Open asset in asset database'
              tooltipTitle='Open Asset'
            />
          )}
        </LinkRow>,
      ] as const,
      [
        'Analysis Date',
        <EditableDate
          key='analysisDate'
          value={niiData?.analysisDate}
          onChange={setAnalysisDate}
          referenceValues={analysisDateReferenceValues}
        />,
      ],
    ],
    [
      assetModelData.siteName,
      assetModelData.unit,
      siteId,
      unitId,
      niiData?.analysisDate,
      setAnalysisDate,
      analysisDateReferenceValues,
    ]
  )

  return (
    <StyledCard title='Basic Information'>
      <OptionalDescriptions items={items} isLoading={isLoading} />
    </StyledCard>
  )
}

export const NiiBasicInformationCard = React.memo(RawNiiBasicInformationCard)
